import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888', surface: '#111111' };

const STOREFRONT_URL = import.meta.env.VITE_STOREFRONT_URL || 'https://qrqy.shop';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// In mock mode the session user comes from App.jsx; Dashboard uses inline mock for display
const MOCK_USER = { name: import.meta.env.VITE_MOCK_AUTH === 'true' ? 'DEMO OWNER' : 'OWNER' };

// TODO(M3): Replace with real data from GET /api/dashboard/stats
const MOCK_STATS = {
  totalQRs: 2,
  scansThisWeek: 47,
  mostScannedCode: 'XQ7R4P',
  mostScannedCount: 31,
};

// TODO(M3): Replace with real data from GET /api/qr-codes?owner=me&limit=5
const MOCK_RECENT_ACTIVITY = [
  { code: 'XQ7R4P', productName: 'QR TEE', scans: 31, lastDestination: 'open.spotify.com/album/...' },
  { code: 'KN2M8A', productName: 'QR HOODIE', scans: 16, lastDestination: 'instagram.com/p/...' },
];

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      padding: '1.5rem',
      border: `1px solid rgba(255,255,255,0.08)`,
      backgroundColor: COLORS.surface,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    }}>
      <p style={{
        fontSize: '0.62rem',
        letterSpacing: '0.18em',
        color: COLORS.gray,
        margin: 0,
        fontWeight: 600,
      }}>
        {label}
      </p>
      <p style={{
        fontSize: '2rem',
        fontWeight: 900,
        color: COLORS.white,
        margin: 0,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </p>
      {sub && (
        <p style={{ fontSize: '0.72rem', color: COLORS.gray, margin: 0 }}>
          {sub}
        </p>
      )}
    </div>
  );
}

/**
 * ClaimBanner — shown when ?claim=<token> is present in the URL.
 * Calls POST /api/claims/redeem and displays success or error.
 */
function ClaimBanner({ claimToken, onDismiss }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [code, setCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!claimToken) return;

    // Remove ?claim= from URL immediately so a refresh doesn't re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete('claim');
    window.history.replaceState({}, '', url.pathname + (url.search || ''));

    if (!API_BASE_URL) {
      // Mock mode — simulate success
      setTimeout(() => {
        setCode('MOCKQR');
        setStatus('success');
      }, 600);
      return;
    }

    fetch(`${API_BASE_URL}/api/claims/redeem`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ claim_token: claimToken }),
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          setCode(data.code);
          setStatus('success');
        } else {
          setErrorMsg(data.error || 'Failed to activate ownership. Please try again.');
          setStatus('error');
        }
      })
      .catch(() => {
        setErrorMsg('Network error. Check your connection and try again.');
        setStatus('error');
      });
  }, [claimToken]);

  if (status === 'loading') {
    return (
      <div style={{
        padding: '1.25rem 1.5rem',
        border: `1px solid rgba(201,168,76,0.3)`,
        backgroundColor: 'rgba(201,168,76,0.04)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <p style={{ fontSize: '0.75rem', letterSpacing: '0.15em', color: COLORS.gold, margin: 0, fontWeight: 600 }}>
          ACTIVATING OWNERSHIP...
        </p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={{
        padding: '1.5rem',
        border: `1px solid ${COLORS.gold}`,
        backgroundColor: 'rgba(201,168,76,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}>
        <p style={{ fontSize: '0.62rem', letterSpacing: '0.2em', color: COLORS.gold, margin: 0, fontWeight: 700 }}>
          OWNERSHIP ACTIVATED
        </p>
        <p style={{ fontSize: '0.88rem', color: COLORS.white, margin: 0, fontWeight: 700 }}>
          QR code <span style={{ color: COLORS.gold, fontVariantNumeric: 'tabular-nums' }}>{code}</span> is now yours.
        </p>
        <p style={{ fontSize: '0.78rem', color: COLORS.gray, margin: 0, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
          Your piece is linked. Edit the destination, see scan analytics, and manage it from My QR Codes.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {code && (
            <Link
              to={`/qr/${code}`}
              style={{
                display: 'inline-block',
                padding: '0.6rem 1.5rem',
                backgroundColor: COLORS.gold,
                color: COLORS.black,
                textDecoration: 'none',
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.18em',
              }}
            >
              EDIT QR {code}
            </Link>
          )}
          <button
            onClick={onDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.35)',
              fontSize: '0.65rem',
              letterSpacing: '0.1em',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            DISMISS
          </button>
        </div>
      </div>
    );
  }

  // error
  return (
    <div style={{
      padding: '1.25rem 1.5rem',
      border: '1px solid rgba(224,85,85,0.3)',
      backgroundColor: 'rgba(224,85,85,0.05)',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    }}>
      <p style={{ fontSize: '0.62rem', letterSpacing: '0.2em', color: '#e05555', margin: 0, fontWeight: 700 }}>
        ACTIVATION FAILED
      </p>
      <p style={{ fontSize: '0.82rem', color: '#e05555', margin: 0 }}>{errorMsg}</p>
      <button
        onClick={onDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.35)',
          fontSize: '0.65rem',
          letterSpacing: '0.1em',
          cursor: 'pointer',
          padding: 0,
          alignSelf: 'flex-start',
        }}
      >
        DISMISS
      </button>
    </div>
  );
}

export default function Dashboard() {
  const user = MOCK_USER;
  const hasQRs = MOCK_RECENT_ACTIVITY.length > 0;

  // Detect ?claim= token on mount
  const [claimToken, setClaimToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('claim') || null;
  });

  function handleClaimDismiss() {
    setClaimToken(null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
      {/* Claim activation banner */}
      {claimToken && (
        <ClaimBanner claimToken={claimToken} onDismiss={handleClaimDismiss} />
      )}

      {/* Greeting */}
      <div>
        <p style={{
          fontSize: '0.65rem',
          letterSpacing: '0.25em',
          color: COLORS.gold,
          margin: '0 0 0.5rem',
          fontWeight: 600,
        }}>
          OVERVIEW
        </p>
        <h1 style={{
          fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
          fontWeight: 900,
          letterSpacing: '-0.01em',
          color: COLORS.white,
          margin: '0 0 0.5rem',
        }}>
          WELCOME BACK, {user.name}.
        </h1>
        <p style={{
          fontSize: '0.88rem',
          color: COLORS.gray,
          margin: 0,
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
        }}>
          Here's what's scanning.
        </p>
      </div>

      {/* Stats row */}
      {/* TODO(M3): Replace MOCK_STATS with real API data */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1px',
        backgroundColor: 'rgba(255,255,255,0.06)',
      }}>
        <StatCard
          label="QRS OWNED"
          value={MOCK_STATS.totalQRs}
          sub="Active pieces"
        />
        <StatCard
          label="SCANS THIS WEEK"
          value={MOCK_STATS.scansThisWeek}
          sub="Across all pieces"
        />
        <StatCard
          label="TOP PIECE"
          value={MOCK_STATS.mostScannedCode}
          sub={`${MOCK_STATS.mostScannedCount} scans all-time`}
        />
      </div>

      {/* Recent activity */}
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
        }}>
          <p style={{
            fontSize: '0.65rem',
            letterSpacing: '0.2em',
            color: COLORS.white,
            margin: 0,
            fontWeight: 700,
          }}>
            RECENT ACTIVITY
          </p>
          <Link
            to="/my-qrs"
            style={{
              fontSize: '0.65rem',
              letterSpacing: '0.12em',
              color: COLORS.gold,
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            VIEW ALL →
          </Link>
        </div>

        {hasQRs ? (
          <div style={{ border: `1px solid rgba(255,255,255,0.08)` }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 80px 1fr',
              gap: '1rem',
              padding: '0.75rem 1.25rem',
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
              backgroundColor: COLORS.surface,
            }}>
              {['CODE', 'PIECE', 'SCANS', 'LAST DESTINATION'].map((h) => (
                <span key={h} style={{
                  fontSize: '0.6rem',
                  letterSpacing: '0.15em',
                  color: COLORS.gray,
                  fontWeight: 700,
                }}>
                  {h}
                </span>
              ))}
            </div>
            {/* Table rows */}
            {MOCK_RECENT_ACTIVITY.map((row) => (
              <div
                key={row.code}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 80px 1fr',
                  gap: '1rem',
                  padding: '1rem 1.25rem',
                  borderBottom: `1px solid rgba(255,255,255,0.04)`,
                  alignItems: 'center',
                }}
              >
                <Link to={`/qr/${row.code}`} style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: COLORS.gold,
                  textDecoration: 'none',
                  letterSpacing: '0.1em',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {row.code}
                </Link>
                <span style={{ fontSize: '0.78rem', color: COLORS.white, letterSpacing: '0.06em' }}>
                  {row.productName}
                </span>
                <span style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: row.scans > 20 ? COLORS.gold : COLORS.white,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {row.scans}
                </span>
                <span style={{
                  fontSize: '0.72rem',
                  color: COLORS.gray,
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {row.lastDestination}
                </span>
              </div>
            ))}
          </div>
        ) : (
          /* Empty state */
          <div style={{
            padding: '4rem',
            textAlign: 'center',
            border: `1px solid rgba(255,255,255,0.06)`,
          }}>
            <p style={{
              fontSize: '0.88rem',
              color: COLORS.gray,
              margin: '0 0 1.5rem',
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
            }}>
              You don't have any pieces yet.
            </p>
            <a
              href={STOREFRONT_URL + '/shop'}
              style={{
                display: 'inline-block',
                padding: '0.75rem 2rem',
                backgroundColor: COLORS.gold,
                color: COLORS.black,
                textDecoration: 'none',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.18em',
              }}
            >
              VISIT THE SHOP
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
